import { Region, SagaMode, useSagaAuth } from "@einride/saga-core"
import { Logger } from "lib/logging"

// Production refers to the domain .tech
const isProduction = import.meta.env.MODE === "production"
// Staging refers to the domain .dev
const isStaging = import.meta.env.MODE === "staging"

export const getSagaMode = (): SagaMode => {
  if (isProduction) return "production"
  if (isStaging) return "staging"
  return "development"
}

// values that are the same for all regions goes here
// Any variable that will vary between environments need to come from an environment variable.
const base = {
  analyticsKey: import.meta.env.VITE_ANALYTICS_KEY,
  environment: getSagaMode(),
}

// EU specific values goes here
// Any variable that will vary between environments need to come from an environment variable.
const EU = {
  ...base,
  region: "EU",
  baseUrl: import.meta.env.VITE_API_EU_BASE_URL ?? "http://localhost:8081",
}

// US specific values goes here.
// Any variable that will vary between environments need to come from an environment variable.
const US = {
  ...base,
  region: "US",
  baseUrl: import.meta.env.VITE_API_US_BASE_URL ?? "http://localhost:8081",
}

type BaseConfig = typeof base
type Config = typeof EU & typeof US

const getRegionalConfig = (region: Region): Config => {
  switch (region) {
    case "EU":
      return EU
    case "US":
      return US
    default:
      Logger.error("unexpectedly defaulted to EU region for logged in user")
      return EU
  }
}

/**
 * This method returns configuration variables not specific for a region
 *
 * @function
 * @name getConfig
 * @return {BaseConfig} Configuration variables non-region specific
 */
export const getConfig = (): BaseConfig => base

/**
 * This method returns region specific configuration variables.
 *
 * @hook
 * @name useConfig
 * @return {Config} Region specific configuration variables
 */
export const useConfig = (): Config => {
  const auth = useSagaAuth()
  if (!auth?.activeOrganization?.region) {
    // this cannot happen when calling useConfig under a protected route (SagaPrivateRoutes)
    // as it blocks rendering until the region is set
    throw new Error("organization not set")
  }
  return getRegionalConfig(auth.activeOrganization.region)
}
